import React, { useState, useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import config from 'config';
import { sanitizeFleetIdentifier } from '../../../utils/FleetHelpers';
import {
  StyledEditor,
  StyledEditorBody,
  StyledGrid,
  StyledGridThree,
  StyledField,
  StyledLabel,
  StyledRequiredMark,
  StyledInput,
  StyledSelect,
  StyledFooter,
  StyledCancelButton,
  StyledSaveButton,
} from './FleetEditorStyled';

const StyledSingleField = styled(StyledField)({
  marginBottom: '0.9rem',
});

const {
  scenario: {
    vehicle: { costs: defaultVehicleCosts },
  },
} = config;

const FleetTypeEditor = ({
  isNew,
  typeName,
  typeId,
  profileName,
  profiles,
  amount,
  capacity,
  costs,
  onSave,
  onCancel,
  labels: {
    typeNameLabel,
    typeIdLabel,
    linkedProfileLabel,
    typeAmountLabel,
    typeCapacityLabel,
    costFixedLabel,
    costTimeLabel,
    costDistanceLabel,
    saveTypeLabel,
    cancelTypeLabel,
    typeNamePlaceholder,
    typeIdPlaceholder,
  },
}) => {
  const [draftName, setDraftName] = useState(isNew ? '' : typeName);
  const [draftId, setDraftId] = useState(isNew ? '' : typeId);
  const [draftProfile, setDraftProfile] = useState(isNew ? profiles?.[0]?.name : profileName);
  const [draftAmount, setDraftAmount] = useState(isNew ? '2' : amount);
  const [draftCapacity, setDraftCapacity] = useState(isNew ? '30' : capacity);
  const [draftCosts, setDraftCosts] = useState(
    isNew
      ? {
          fixed: String(defaultVehicleCosts.fixed),
          time: String(defaultVehicleCosts.time),
          distance: String(defaultVehicleCosts.distance),
        }
      : costs,
  );

  const onNameChange = useCallback((e) => setDraftName(e.target.value), []);

  const onIdChange = useCallback((e) => setDraftId(sanitizeFleetIdentifier(e.target.value)), []);

  const onProfileChange = useCallback((e) => setDraftProfile(e.target.value), []);

  const onAmountChange = useCallback((e) => setDraftAmount(e.target.value), []);

  const onCapacityChange = useCallback((e) => setDraftCapacity(e.target.value), []);

  const onFixedCostChange = useCallback(
    (e) => setDraftCosts((prev) => ({ ...prev, fixed: e.target.value })),
    [],
  );

  const onTimeCostChange = useCallback(
    (e) => setDraftCosts((prev) => ({ ...prev, time: e.target.value })),
    [],
  );

  const onDistanceCostChange = useCallback(
    (e) => setDraftCosts((prev) => ({ ...prev, distance: e.target.value })),
    [],
  );

  const onSaveClick = useCallback(() => {
    const toNumber = (value, fallback) => {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    };

    onSave({
      name: draftName,
      id: draftId,
      profile: draftProfile,
      amount: toNumber(draftAmount, 1),
      capacity: toNumber(draftCapacity, 1),
      costs: {
        fixed: toNumber(draftCosts.fixed, 0),
        time: toNumber(draftCosts.time, 0),
        distance: toNumber(draftCosts.distance, 0),
      },
    });
  }, [onSave, draftName, draftId, draftProfile, draftAmount, draftCapacity, draftCosts]);

  const isValid = useMemo(() => {
    const amountValue = parseFloat(draftAmount);
    const capacityValue = parseFloat(draftCapacity);
    return (
      (draftName || '').trim().length > 0 &&
      (draftId || '').trim().length > 0 &&
      !!draftProfile &&
      draftAmount !== '' &&
      !Number.isNaN(amountValue) &&
      amountValue > 0 &&
      draftCapacity !== '' &&
      !Number.isNaN(capacityValue) &&
      capacityValue > 0
    );
  }, [draftName, draftId, draftProfile, draftAmount, draftCapacity]);

  return (
    <StyledEditor>
      <StyledEditorBody>
        <StyledGrid>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-name">
              {typeNameLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-type-name"
              type="text"
              placeholder={typeNamePlaceholder}
              value={draftName}
              onChange={onNameChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-id">
              {typeIdLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-type-id"
              type="text"
              placeholder={typeIdPlaceholder}
              value={draftId}
              onChange={onIdChange}
            />
          </StyledField>
        </StyledGrid>
        <StyledSingleField>
          <StyledLabel htmlFor="input-fleet-type-profile">
            {linkedProfileLabel}
            <StyledRequiredMark>*</StyledRequiredMark>
          </StyledLabel>
          <StyledSelect
            id="input-fleet-type-profile"
            value={draftProfile}
            onChange={onProfileChange}
          >
            {profiles.map(({ name }) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </StyledSelect>
        </StyledSingleField>
        <StyledGrid>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-amount">
              {typeAmountLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-type-amount"
              type="number"
              min="1"
              placeholder="5"
              value={draftAmount}
              onChange={onAmountChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-capacity">
              {typeCapacityLabel}
              <StyledRequiredMark>*</StyledRequiredMark>
            </StyledLabel>
            <StyledInput
              id="input-fleet-type-capacity"
              type="number"
              min="1"
              placeholder="300"
              value={draftCapacity}
              onChange={onCapacityChange}
            />
          </StyledField>
        </StyledGrid>
        <StyledGridThree>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-cost-fixed">{costFixedLabel}</StyledLabel>
            <StyledInput
              id="input-fleet-type-cost-fixed"
              type="number"
              min="0"
              placeholder="22"
              value={draftCosts.fixed}
              onChange={onFixedCostChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-cost-time">{costTimeLabel}</StyledLabel>
            <StyledInput
              id="input-fleet-type-cost-time"
              type="number"
              min="0"
              placeholder="7"
              value={draftCosts.time}
              onChange={onTimeCostChange}
            />
          </StyledField>
          <StyledField>
            <StyledLabel htmlFor="input-fleet-type-cost-distance">{costDistanceLabel}</StyledLabel>
            <StyledInput
              id="input-fleet-type-cost-distance"
              type="number"
              min="0"
              step="0.1"
              placeholder="0.5"
              value={draftCosts.distance}
              onChange={onDistanceCostChange}
            />
          </StyledField>
        </StyledGridThree>
      </StyledEditorBody>
      <StyledFooter>
        <StyledCancelButton id="button-fleet-type-cancel" type="button" onClick={onCancel}>
          {cancelTypeLabel}
        </StyledCancelButton>
        <StyledSaveButton
          id="button-fleet-type-save"
          type="button"
          disabled={!isValid}
          onClick={onSaveClick}
        >
          {saveTypeLabel}
        </StyledSaveButton>
      </StyledFooter>
    </StyledEditor>
  );
};

export default FleetTypeEditor;
